import { useEffect, useRef } from "react";

/**
 * Google Translate widget — versão idempotente (carrega o script Google
 * Website Translator uma única vez por sessão). Mostra um <select> com idiomas
 * que traduz o conteúdo da página inteira no cliente.
 *
 * Notes:
 * - O Google injecta um banner sticky no topo do <body> (`.skiptranslate`) que
 *   empurra o layout. O CSS injectado abaixo esconde esse banner e mantém apenas
 *   o widget que renderizamos aqui.
 * - O atributo `translate="no"` em elementos críticos (logos, números) evita
 *   que sejam alterados pela tradução automática.
 */
const SCRIPT_ID = "google-translate-script";
const STYLE_ID = "google-translate-style";
const TARGET_ID_PREFIX = "google_translate_element";

function ensureGlobalStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    /* Hide google translate top banner & body offset (browser nudge) */
    .skiptranslate iframe.goog-te-banner-frame,
    iframe.goog-te-banner-frame { display: none !important; }
    body { top: 0 !important; position: static !important; }
    /* Hide the "Powered by Google" link/logo and helper text */
    .goog-logo-link, .goog-te-gadget > span,
    .VIpgJd-ZVi9od-l4eHX-hSRGPd { display: none !important; }
    .goog-te-gadget {
      font-size: 0 !important;
      line-height: 0 !important;
      color: transparent !important;
    }
    /* Style the actual <select> dropdown */
    .goog-te-gadget .goog-te-combo {
      margin: 0 !important;
      padding: 6px 10px !important;
      border-radius: 8px !important;
      border: 1px solid #E5E7EB !important;
      background: #FFFFFF !important;
      color: #0F1829 !important;
      font-family: 'Manrope', sans-serif !important;
      font-size: 12px !important;
      line-height: 1.2 !important;
      cursor: pointer !important;
      max-width: 150px !important;
      outline: none !important;
    }
    .goog-te-gadget .goog-te-combo:hover { border-color: #0F1829 !important; }
    .goog-te-gadget .goog-te-combo:focus { border-color: #0F1829 !important; box-shadow: 0 0 0 2px rgba(15,24,41,0.1) !important; }
    iframe.goog-te-menu-frame { box-shadow: 0 6px 20px rgba(15,24,41,0.15) !important; }
  `;
  document.head.appendChild(style);
}

function loadScriptOnce(callbackName) {
  if (window.google?.translate?.TranslateElement) return Promise.resolve();
  if (document.getElementById(SCRIPT_ID)) {
    // Already loading — wait for it
    return new Promise((resolve) => {
      const t = setInterval(() => {
        if (window.google?.translate?.TranslateElement) {
          clearInterval(t);
          resolve();
        }
      }, 150);
    });
  }
  return new Promise((resolve) => {
    window[callbackName] = () => resolve();
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://translate.google.com/translate_a/element.js?cb=${callbackName}`;
    s.async = true;
    document.body.appendChild(s);
  });
}

let instanceCounter = 0;

/**
 * Marca como `notranslate` (atributo HTML respeitado pelo Google Translate)
 * elementos que contêm conteúdo técnico ou numérico que não deve ser
 * alterado pela tradução. Selectores:
 *   - `.font-mono` (códigos, IBANs, NIFs, GUIDs já em monospace)
 *   - `.tabular-nums` (valores monetários, referências MB, IBAN/SWIFT)
 *   - `[data-no-translate]` (marker explícito)
 *   - `[data-testid*="entidade"]`, `[data-testid*="ref"]`, `[data-testid*="iban"]`,
 *     `[data-testid*="swift"]`, `[data-testid*="nif"]` (campos críticos de pagamento)
 */
const AUTOPROTECT_SELECTORS = [
  ".font-mono",
  ".tabular-nums",
  "[data-no-translate]",
  '[data-testid*="iban"]',
  '[data-testid*="swift"]',
  '[data-testid*="entidade"]',
  '[data-testid*="referencia"]',
  '[data-testid*="-ref"]',
  '[data-testid*="nif"]',
].join(",");

function applyNoTranslate(root) {
  try {
    const nodes = root.querySelectorAll(AUTOPROTECT_SELECTORS);
    nodes.forEach((el) => {
      if (!el.classList.contains("notranslate")) {
        el.classList.add("notranslate");
        el.setAttribute("translate", "no");
      }
    });
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("applyNoTranslate skipped:", e);
  }
}

let observer = null;
function ensureNoTranslateObserver() {
  if (observer) return;
  applyNoTranslate(document.body);
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (n.nodeType === 1 /* ELEMENT_NODE */) {
          if (n.matches?.(AUTOPROTECT_SELECTORS)) {
            n.classList.add("notranslate");
            n.setAttribute("translate", "no");
          }
          applyNoTranslate(n);
        }
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export default function GoogleTranslate({ className = "" }) {
  const containerRef = useRef(null);
  const idRef = useRef(`${TARGET_ID_PREFIX}_${++instanceCounter}`);

  useEffect(() => {
    let cancelled = false;
    ensureGlobalStyles();
    ensureNoTranslateObserver();
    const callbackName = "__googleTranslateInit";
    loadScriptOnce(callbackName).then(() => {
      if (cancelled || !containerRef.current) return;
      try {
        // eslint-disable-next-line no-new, new-cap
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "pt",
            includedLanguages: "pt,en,es,fr,de,it,nl,pl,ru,uk,zh-CN,ja,ar",
            autoDisplay: false,
          },
          idRef.current
        );
      } catch (e) {
        // Silenciar erros do widget (rede offline, etc.) mas logar em dev.
        if (process.env.NODE_ENV !== "production") console.warn("Google Translate init skipped:", e);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id={idRef.current}
      data-testid="google-translate-widget"
      className={`google-translate-host ${className}`}
      translate="no"
    />
  );
}
